import { useAuth0 } from "@auth0/auth0-react";
import { Info, Security } from "@mui/icons-material";
import {
    Alert,
    Box,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useQueryOptionsFactory } from "@/queries";
import { adminRole, ownerRole } from "@/utils/roles.ts";

const AdminDashboard = (): ReactNode => {
    const features = [
        {
            title: "All Units & Owners",
            description:
                "A list of all Units and their Owners in the database. You can sort by Owner Name or Unit Number, and can View each Unit's Calendar, View and Edit Unit data, and Delete Units if necessary. For Editing any Unit data, you would come here.",
        },
        {
            title: "New Unit Info",
            description:
                'Here is where Owner information for a new Unit can be added. Each Unit should be entered only once. To Edit the Unit information, see "All Owners & Units" above.',
        },
        {
            title: "Unit Information",
            description: "Information for each Unit sortable numerous ways.",
        },
        {
            title: "Find Reservation",
            description:
                "Search for reservations by Guest Name, Owner Name, Unit number, or sort the data in numerous ways.",
        },
        {
            title: "Guest Report",
            description: "Sortable data for Guests ... and searchable by Guest Name.",
        },
        {
            title: "Gate Code",
            description:
                "It is possible here to view and edit the Gate Codes for the various dates.",
        },
    ];

    return (
        <Grid container sx={{ maxWidth: { xs: "100%", md: 900 }, margin: "auto" }}>
            <Grid
                size={{ xs: 12 }}
                sx={{
                    backgroundColor: "white",
                    borderRadius: 3,
                    boxShadow: 2,
                    p: { xs: 2, md: 4 },
                    mb: 3,
                }}
            >
                <Grid container sx={{ alignItems: "center", mb: 2 }}>
                    <Info sx={{ color: "#28638c", mr: 2, fontSize: { xs: 24, md: 28 } }} />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            background: "linear-gradient(45deg, #28638c, #1e4a66)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            fontSize: { xs: "1.1rem", md: "1.25rem" },
                        }}
                    >
                        Welcome to the Mahana Renter Database
                    </Typography>
                </Grid>

                <Alert
                    severity="warning"
                    icon={<Security />}
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        "& .MuiAlert-message": { fontSize: "0.9rem" },
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        IMPORTANT
                    </Typography>
                    <Typography variant="body2">
                        The information contained within this database is confidential and intended
                        solely for performing functions within and pertaining to this Guest
                        Registration System. The user of this database shall not use this
                        information for any other purpose, nor share any portion of it with any
                        other individual not approved for access.
                    </Typography>
                </Alert>

                <Typography variant="subtitle1" sx={{ mb: 2, color: "#1e293b", fontWeight: 600 }}>
                    Here is a little explanation of what you can see and do here:
                </Typography>

                <List sx={{ "& .MuiListItem-root": { alignItems: "flex-start", py: 1 } }}>
                    {features.map((feature, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }}>
                            <ListItemText
                                primary={
                                    <Grid container sx={{ alignItems: "center", mb: 0.5 }}>
                                        <Chip
                                            label={feature.title}
                                            size="small"
                                            sx={{
                                                backgroundColor: "#28638c",
                                                color: "white",
                                                fontWeight: 500,
                                                mr: 2,
                                                fontSize: { xs: "0.75rem", md: "0.8rem" },
                                            }}
                                        />
                                    </Grid>
                                }
                                secondary={
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#64748b",
                                            lineHeight: 1.5,
                                            fontSize: { xs: "0.8rem", md: "0.85rem" },
                                            ml: 0,
                                        }}
                                    >
                                        {feature.description}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>

                <Grid
                    size={{ xs: 12 }}
                    sx={{
                        backgroundColor: "#f8fafc",
                        borderLeft: "4px solid #28638c",
                        borderRadius: 2,
                        boxShadow: 1,
                        p: { xs: 2, md: 2.5 },
                        mt: 3,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#475569",
                            lineHeight: 1.5,
                            fontSize: { xs: "0.8rem", md: "0.85rem" },
                        }}
                    >
                        The database is built around the Units, not the Owners. This means each Unit
                        in this rental program must be added to the database, and if an Owner has
                        several units, his information will be added for each unit, and will have a
                        different password for each in order to manage them separately.
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

const OwnerDashboard = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { data } = useSuspenseQuery(qof.units.list());
    const units = data.data;
    const ownerName = `${units[0]?.owner?.ownerFirstName} ${units[0]?.owner?.ownerName}`;

    const features = [
        {
            title: "Book Reservation",
            description:
                'You may add a Guest Reservation with the "Book Reservation" link on the left.',
        },
        {
            title: "Find Reservation",
            description:
                'Under "Find Reservation" you can see all past reservations, which can be ordered or searched for in several ways. Under Info/Edit you can view extended information about any reservation, and edit it if you wish. There is a Delete button for reservations which are not valid.',
        },
        {
            title: "Edit Personal Info",
            description:
                'Under "Edit Personal Info" you can view and change information relating to you in the database.',
        },
        {
            title: "My Units",
            description:
                'Under "My Units" you can view and change information relating to one of your units.',
        },
    ];

    return (
        <Box sx={{ maxWidth: { xs: "100%", md: 900 }, margin: "auto" }}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        background: "linear-gradient(45deg, #28638c, #1e4a66)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontSize: { xs: "1.5rem", md: "2rem" },
                        mb: 1,
                    }}
                >
                    Aloha {ownerName},
                </Typography>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: "#f59e0b",
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        mb: 3,
                    }}
                >
                    Welcome to the Mahana Guest Registration System
                </Typography>

                <List sx={{ "& .MuiListItem-root": { alignItems: "flex-start", py: 2 } }}>
                    {features.map((feature, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            mb: 0.5,
                                            fontSize: { xs: "1rem", md: "1.1rem" },
                                        }}
                                    >
                                        • {feature.title}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#64748b",
                                            lineHeight: 1.6,
                                            fontSize: { xs: "0.9rem", md: "1rem" },
                                            ml: 2,
                                        }}
                                    >
                                        {feature.description}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>
        </Box>
    );
};

const Root = (): ReactNode => {
    const { user } = useAuth0();

    if (user?.globalRoles.includes(adminRole)) {
        return <AdminDashboard />;
    }

    if (user?.globalRoles.includes(ownerRole)) {
        return <OwnerDashboard />;
    }

    return <></>;
};

export const Route = createFileRoute("/")({
    component: Root,
});
